import { FC } from 'react';
import { Checkbox } from '@carbon/react';
import { formatNumber } from 'utils/number';
import type {
  BuilderSpacing,
  BuilderSpacingState,
} from './use-builder-spacing';
import style from './unit-builder-modal.module.scss';

interface UnitBuilderSpacingControlsProps {
  spacing: BuilderSpacingState;
  /** Promoted workspaces read the values rather than edit them. */
  editable?: boolean;
}

type SpacingKey = keyof BuilderSpacing;

const FIELDS: { key: SpacingKey; label: string }[] = [
  { key: 'wellSpacingFt', label: 'Unit Well Spacing (ft):' },
  { key: 'crossLineSetbackFt', label: 'Cross-line Setback (ft):' },
  { key: 'inLineSetbackFt', label: 'In-line Setback (ft):' },
  { key: 'spacingToleranceFt', label: 'Spacing Tolerance (ft):' },
];

/**
 * The gun barrel pane's controls strip.
 *
 * Plain inputs with the label inline, as the mock has them: a Carbon field would
 * put the label above and render an underline control, which reads quite
 * differently at this size. Values are text rather than number so they can carry
 * a thousands separator ("1,320"); digits are parsed back on edit. The repo
 * forbids raw `toLocaleString` on numbers, so formatting goes through
 * `formatNumber`.
 *
 * @param {UnitBuilderSpacingControlsProps} props - Spacing state and edit mode.
 * @return {JSX.Element} The controls strip.
 */
export const UnitBuilderSpacingControls: FC<
  UnitBuilderSpacingControlsProps
> = ({ spacing, editable = true }) => (
  <div className={style.spacingControls}>
    <div className={style.spacingRow}>
      {FIELDS.map(({ key, label }) => (
        <label key={key} className={style.spacingLabel}>
          {label}
          <input
            type="text"
            inputMode="numeric"
            className={style.spacingInput}
            readOnly={!editable}
            value={
              spacing[key] !== null && Number.isFinite(spacing[key])
                ? formatNumber({
                    value: spacing[key] as number,
                    minPrecision: 0,
                  })
                : ''
            }
            onChange={(event) => {
              const digits = event.target.value.replace(/[^\d]/g, '');
              spacing.set({ [key]: digits === '' ? null : Number(digits) });
            }}
          />
        </label>
      ))}

      {spacing.wellsPerMile !== null && (
        <span className={style.wellsPerMile}>
          {spacing.wellsPerMile} wells per mile
        </span>
      )}
    </div>

    <Checkbox
      id="unit-builder-ignore-existing-development"
      labelText="Ignore Existing Development"
      checked={spacing.ignoreExistingDevelopment}
      disabled={!editable}
      onChange={(_event, { checked }) =>
        spacing.setIgnoreExistingDevelopment(checked)
      }
    />
  </div>
);
